// src/pages/Signup.jsx
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import handwritingImage from '../assets/images/pexels-cottonbro-2925316.jpg';

const Signup = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password2: '',
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [signupError, setSignupError] = useState('');
  const [signupSuccess, setSignupSuccess] = useState(false);
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    else if (formData.name.length < 3) newErrors.name = 'Name must be at least 3 characters';

    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';

    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 8)
      newErrors.password = 'Password must be at least 8 characters';
    else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])/.test(formData.password))
      newErrors.password = 'Password needs lowercase, uppercase, number & special char';

    if (!formData.password2) newErrors.password2 = 'Please confirm your password';
    else if (formData.password !== formData.password2)
      newErrors.password2 = 'Passwords do not match';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: '' });
    if (signupError) setSignupError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setSignupError('');
    setErrors({});

    try {
      const response = await fetch('https://app.writeapp.in/auth/register', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          is_active: true,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSignupSuccess(true);
      } else {
        setSignupError(data.detail || data.message || 'Signup failed.');
      }
    } catch (error) {
      console.error('Signup error:', error);
      setSignupError('Server error. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0C0D] flex items-center justify-center p-4 sm:p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-5xl bg-dark-gray/30 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden shadow-xl flex flex-col md:flex-row"
      >
        <div className="w-full md:w-1/2 p-6 sm:p-8">
          <Link
            to="/"
            className="flex items-center text-gray-400 hover:text-white mb-6 transition-colors duration-200 group"
          >
            <svg
              className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform duration-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Home
          </Link>

          <h1 className="text-2xl font-bold text-white mb-6">Create an Account</h1>

          {signupError && (
            <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded mb-4">
              {signupError}
            </div>
          )}

          {signupSuccess ? (
            <div className="bg-green-500/20 border border-green-500/50 text-green-200 px-6 py-8 rounded-lg text-center space-y-4">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-500/20 mb-4">
                <svg className="h-6 w-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white">Check Your Email</h2>
              <p className="text-gray-300">
                We've sent a verification link to <span className="font-semibold text-white">{formData.email}</span>.
              </p>
              <p className="text-sm text-gray-400">
                Please click the link to verify your account. You will be able to log in after verification.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <InputField
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={errors.name}
              />
              <InputField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
              />
              <InputField
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                error={errors.password}
              />
              <InputField
                label="Confirm Password"
                name="password2"
                type="password"
                value={formData.password2}
                onChange={handleChange}
                error={errors.password2}
              />

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full bg-primary hover:bg-blue-600 text-white py-2 rounded-md transition-colors duration-200 flex items-center justify-center ${
                  isLoading ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Creating Account...
                  </>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>
          )}

          <div className="mt-6 text-center text-gray-400">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:text-blue-400 transition-colors">
              Log In
            </Link>
          </div>
        </div>

        <div className="md:w-1/2 relative hidden md:block">
          <img
            src={handwritingImage}
            alt="Handwriting analysis"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent flex items-start justify-center p-8 pt-16">
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-4">Discover the Art of Handwriting Analysis</h2>
              <p className="text-gray-300">
                Signup to our website and start analyzing handwriting samples today!
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const InputField = ({ label, name, type = 'text', value, onChange, error }) => (
  <div>
    <label htmlFor={name} className="block text-gray-300 mb-2">
      {label}
    </label>
    <input
      type={type}
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      className={`w-full bg-dark-gray/50 border ${
        error ? 'border-red-500' : 'border-gray-700'
      } rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary`}
      placeholder={label}
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

export default Signup;
